import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface StripeError {
  message: string;
  code?: string;
}

export function useStripe() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<StripeError | null>(null);

  /**
   * Create a Stripe Checkout session and redirect to payment page
   * @param priceType - 'monthly' or 'annual'
   */
  const createCheckout = async (priceType: 'monthly' | 'annual' = 'monthly') => {
    setLoading(true);
    setError(null);

    try {
      const response = await supabase.functions.invoke('create-checkout', {
        body: { priceType, origin: window.location.origin },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create checkout session');
      }

      if (!response.data?.url) {
        throw new Error('No checkout URL returned');
      }

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err) {
      const error = err as Error;
      console.error('Checkout error:', error);
      setError({ message: error.message || 'An error occurred during checkout' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open Stripe Customer Portal for subscription management
   */
  const openBillingPortal = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await supabase.functions.invoke('create-portal', {
        body: { origin: window.location.origin },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to open billing portal');
      }

      if (!response.data?.url) {
        throw new Error('No portal URL returned');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = response.data.url;
    } catch (err) {
      const error = err as Error;
      console.error('Portal error:', error);
      setError({ message: error.message || 'An error occurred opening billing portal' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createCheckout,
    openBillingPortal,
    loading,
    error,
    clearError: () => setError(null),
  };
}
